import { useCallback } from 'react';
import { knowledgeApi } from '../lib/knowledge-api';

/**
 * Custom hook for refreshing collections
 * Provides reusable logic for updating loaded collections state
 */
export function useCollectionRefresh(setLoadedCollections) {
  /**
   * Refresh a single collection by ID
   */
  const refreshCollection = useCallback(async (collectionId) => {
    try {
      const updatedCollection = await knowledgeApi.collections.getById(collectionId);
      setLoadedCollections(prev => ({
        ...prev,
        [collectionId]: updatedCollection
      }));
      return updatedCollection;
    } catch (error) {
      console.error(`Failed to refresh collection ${collectionId}:`, error);
      throw error;
    }
  }, [setLoadedCollections]);

  /**
   * Refresh multiple collections in parallel
   */
  const refreshCollections = useCallback(async (collectionIds) => {
    if (!collectionIds || collectionIds.length === 0) return [];

    try {
      // Fetch all collections in parallel
      const results = await Promise.allSettled(
        collectionIds.map(id => knowledgeApi.collections.getById(id))
      );

      // Update state with successful fetches
      const successfulUpdates = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successfulUpdates[collectionIds[index]] = result.value;
        } else {
          console.error(`Failed to refresh collection ${collectionIds[index]}:`, result.reason);
        }
      });

      if (Object.keys(successfulUpdates).length > 0) {
        setLoadedCollections(prev => ({
          ...prev,
          ...successfulUpdates
        }));
      }

      return results;
    } catch (error) {
      console.error('Failed to refresh collections:', error);
      throw error;
    }
  }, [setLoadedCollections]);

  /**
   * Find all loaded collections that contain a specific item
   */
  const findCollectionsWithItem = useCallback((loadedCollections, itemId) => {
    return Object.keys(loadedCollections).filter(collectionId => {
      const collection = loadedCollections[collectionId];
      return collection?.slots?.some(slot => slot.item?.id === itemId);
    });
  }, []);

  /**
   * Refresh all collections that contain a specific item
   */
  const refreshCollectionsWithItem = useCallback(async (loadedCollections, itemId) => {
    const collectionsToRefresh = findCollectionsWithItem(loadedCollections, itemId);

    if (collectionsToRefresh.length === 0) return [];

    return await refreshCollections(collectionsToRefresh);
  }, [findCollectionsWithItem, refreshCollections]);

  return {
    refreshCollection,
    refreshCollections,
    findCollectionsWithItem,
    refreshCollectionsWithItem
  };
}
